/**
 * Servicio para consultar valoraciones de VeCy por dirección.
 *
 * Estrategia de optimización:
 *  - Batch: una sola petición HTTP al backend con N direcciones únicas.
 *  - Caché de sesión (30 min): evita repetir peticiones en la misma sesión.
 */

import {
  buildVecyAddressCandidates,
  resolvePropertyAddress,
} from '@/utils/colivingAddressUtils';

function getApiBaseUrl() {
  // En dev local, el proxy de vue-cli redirige /api → coolliving.io
  if (typeof window !== 'undefined' && /^(localhost|127\.0\.0\.1)$/.test(window.location.hostname)) {
    return '/api';
  }
  return process.env.VUE_APP_API_URL || '/api';
}

const BASE_URL = getApiBaseUrl();

const SESSION_TTL_MS = 30 * 60 * 1000; // 30 min
const BATCH_MAX_ADDRESSES = 50;

/** @type {Map<string, { rating: {score: number, reviewCount: number}|null, expiresAt: number }>} */
const sessionCache = new Map();

function normalizeAddress(address) {
  return address?.trim() || '';
}

function getCachedRating(address) {
  const cached = sessionCache.get(address);
  if (cached && Date.now() < cached.expiresAt) {
    return cached.rating;
  }
  return undefined;
}

function setCachedRating(address, rating) {
  sessionCache.set(address, {
    rating,
    expiresAt: Date.now() + SESSION_TTL_MS,
  });
}

function chunkArray(items, size) {
  const chunks = [];
  for (let i = 0; i < items.length; i += size) {
    chunks.push(items.slice(i, i + size));
  }
  return chunks;
}

async function fetchRatingsBatchFromApi(addresses) {
  try {
    const response = await fetch(`${BASE_URL}/vecy/ratings`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ addresses }),
    });

    if (!response.ok) {
      return Object.fromEntries(addresses.map((address) => [address, null]));
    }

    const data = await response.json();
    const ratings = data?.ratings && typeof data.ratings === 'object' ? data.ratings : {};

    for (const address of addresses) {
      setCachedRating(address, ratings[address] ?? null);
    }

    return ratings;
  } catch {
    const fallback = Object.fromEntries(addresses.map((address) => [address, null]));
    for (const address of addresses) {
      setCachedRating(address, null);
    }
    return fallback;
  }
}

/**
 * Devuelve valoraciones VeCy para un listado de direcciones.
 * Solo pide al backend las direcciones que no están en caché de sesión.
 *
 * @param {string[]} addresses
 * @returns {Promise<Record<string, {score: number, reviewCount: number}|null>>}
 */
export async function getVecyRatingsBatch(addresses) {
  const uniqueAddresses = [...new Set(
    (addresses || []).map(normalizeAddress).filter(Boolean),
  )];

  if (uniqueAddresses.length === 0) return {};

  const result = {};
  const missing = [];

  for (const address of uniqueAddresses) {
    const cached = getCachedRating(address);
    if (cached !== undefined) {
      result[address] = cached;
    } else {
      missing.push(address);
    }
  }

  if (missing.length === 0) return result;

  const chunks = chunkArray(missing, BATCH_MAX_ADDRESSES);
  for (const chunk of chunks) {
    const chunkRatings = await fetchRatingsBatchFromApi(chunk);
    Object.assign(result, chunkRatings);
  }

  return result;
}

/**
 * Compatibilidad: una sola dirección reutiliza el batch interno.
 *
 * @param {string} address
 * @returns {Promise<{score: number, reviewCount: number}|null>}
 */
export async function getVecyRating(address) {
  const key = normalizeAddress(address);
  if (!key) return null;

  const ratings = await getVecyRatingsBatch([key]);
  return ratings[key] ?? null;
}

/**
 * Construye la dirección a enviar a VeCy a partir de un objeto coliving/contract.
 *
 * @param {object} contract
 * @returns {string|null}
 */
export function buildVecyAddress(contract) {
  const resolved = resolvePropertyAddress({
    propertyAddress: contract?.propertyAddress,
    propertyLocation: contract?.propertyLocation,
    propertyNeighborhood: contract?.propertyNeighborhood,
    title: contract?.title,
    source: contract?.source || contract?.externalSource,
    externalSource: contract?.externalSource,
  });

  if (!resolved) return null;

  const location = contract?.propertyLocation?.trim();
  if (location && !resolved.toLowerCase().includes(location.toLowerCase())) {
    return `${resolved}, ${location}`;
  }

  return resolved;
}

/**
 * Variantes de lookup VeCy para un contrato (útil en tests/debug).
 */
export function buildVecyAddressVariants(contract) {
  const primary = buildVecyAddress(contract);
  const candidates = buildVecyAddressCandidates({
    propertyAddress: contract?.propertyAddress,
    propertyLocation: contract?.propertyLocation,
    propertyNeighborhood: contract?.propertyNeighborhood,
    title: contract?.title,
    source: contract?.source || contract?.externalSource,
    externalSource: contract?.externalSource,
  });

  if (primary && !candidates.includes(primary)) {
    return [primary, ...candidates];
  }

  return candidates;
}
